import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Container, Input, Button, VStack, HStack, Heading, useToast, FormControl, FormLabel, 
  IconButton, Select, Spinner, Checkbox, CheckboxGroup, Stack, Box, Divider, Text,
  Tabs, TabList, TabPanels, Tab, TabPanel, Card, CardHeader, CardBody,
  Badge, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useDisclosure, Flex,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  useColorModeValue
} from '@chakra-ui/react'
import { FiPlus, FiTrash, FiMove } from 'react-icons/fi'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import apiClient from '../../../../utils/apiClient'

// Sortable Field Component
function SortableFieldItem({ field, index, onFieldChange, onDelete, getFieldTypeIcon }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `field-${index}` })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const accordionBg = useColorModeValue('white', 'gray.700')
  const accordionHoverBg = useColorModeValue('gray.50', 'gray.600')

  return (
    <AccordionItem
      ref={setNodeRef}
      style={style}
      borderWidth="1px"
      borderRadius="md"
      mb={3}
      bg={accordionBg}
      borderColor={borderColor}
      boxShadow={isDragging ? '0 5px 15px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.1)'}
    >
      <h2>
        <AccordionButton 
          py={3} 
          _hover={{ bg: accordionHoverBg }}
          borderRadius="md"
        >
          <HStack flex="1" textAlign="left" spacing={3}>
            <Box
              {...attributes}
              {...listeners}
              cursor="grab"
              _active={{ cursor: 'grabbing' }}
              display="flex"
              alignItems="center"
              p={1}
              borderRadius="sm"
              _hover={{ bg: 'gray.100' }}
            >
              <FiMove color="gray" size={16} />
            </Box>
            
            <Text fontWeight="medium">
              {getFieldTypeIcon(field.type)} {field.name || `Field ${index + 1}`}
            </Text>
            
            {field.type && (
              <Badge colorScheme="blue" fontSize="xs">
                {field.type}
              </Badge>
            )}
          </HStack>
          
          <HStack spacing={2}>
            <IconButton
              icon={<FiTrash />}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(index);
              }}
              colorScheme="red"
              variant="ghost"
              size="sm"
              aria-label="Delete field"
            />
            <AccordionIcon />
          </HStack>
        </AccordionButton>
      </h2>
      
      <AccordionPanel pb={4}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel>Field Name</FormLabel>
            <Input 
              value={field.name} 
              onChange={(e) => onFieldChange(index, 'name', e.target.value)} 
              placeholder="Enter field name"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Field Type</FormLabel>
            <Select 
              value={field.type} 
              onChange={(e) => onFieldChange(index, 'type', e.target.value)}
            >
              <option value="text">Text</option>
              <option value="richtext">Rich Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="boolean">Boolean</option>
              <option value="select">Select</option>
              <option value="multiselect">Multi Select</option>
              <option value="link">Link</option>
              <option value="file">File</option>
              <option value="tags">Tags</option>
              <option value="json">JSON</option>
            </Select>
          </FormControl>
        </VStack>
      </AccordionPanel>
    </AccordionItem>
  )
}

const EditContentModel = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const cancelRef = useRef()
  const [fieldToDelete, setFieldToDelete] = useState(null)

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')

  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0] || null

  const [model, setModel] = useState({
    tenant_id: tenantId,
    name: '',
    description: '',
    fields: [],
    taxonomy_ids: []
  })
  
  const [loading, setLoading] = useState(true)
  const [availableTaxonomies, setAvailableTaxonomies] = useState([])
  const [loadingTaxonomies, setLoadingTaxonomies] = useState(false)

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (!tenantId) return

    const fetchData = async () => {
      try {
        setLoading(true)
        
        // Fetch content model data
        const modelData = await apiClient(`/content-models/${tenantId}/${id}`)

        // Ensure `fields` is always an array
        let parsedFields = []
        if (Array.isArray(modelData.fields)) {
          parsedFields = modelData.fields
        } else if (typeof modelData.fields === 'string' && modelData.fields.trim().startsWith("[")) {
          try {
            parsedFields = JSON.parse(modelData.fields)
          } catch (error) {
            console.error("Failed to parse fields:", error)
            parsedFields = []
          }
        }
        
        // Process taxonomy IDs
        let taxonomyIds = []
        if (modelData.taxonomy_ids) {
          if (Array.isArray(modelData.taxonomy_ids)) {
            taxonomyIds = modelData.taxonomy_ids
          } else if (typeof modelData.taxonomy_ids === 'string') {
            try {
              taxonomyIds = JSON.parse(modelData.taxonomy_ids)
            } catch (error) {
              console.error("Failed to parse taxonomy_ids:", error)
              taxonomyIds = []
            }
          }
        }

        setModel({
          tenant_id: tenantId,
          name: modelData.name || '',
          description: modelData.description || '',
          fields: parsedFields,
          taxonomy_ids: taxonomyIds
        })
        
        // Fetch available taxonomies
        setLoadingTaxonomies(true)
        try {
          const taxonomiesResponse = await apiClient(`/taxonomies/${tenantId}`)
          
          if (taxonomiesResponse) {
            if (Array.isArray(taxonomiesResponse)) {
              setAvailableTaxonomies(taxonomiesResponse)
            } else if (taxonomiesResponse.taxonomies && Array.isArray(taxonomiesResponse.taxonomies)) {
              setAvailableTaxonomies(taxonomiesResponse.taxonomies)
            } else {
              console.error('Unexpected taxonomy response format:', taxonomiesResponse)
              setAvailableTaxonomies([])
            }
          } else {
            setAvailableTaxonomies([])
          }
        } catch (error) {
          console.error('Error fetching taxonomies:', error)
          setAvailableTaxonomies([])
        } finally {
          setLoadingTaxonomies(false)
        }
      } catch (error) {
        console.error(error)
        toast({
          title: 'Error fetching data',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [tenantId, id, toast])

  const handleChange = (e) => {
    setModel({ ...model, [e.target.name]: e.target.value })
  }

  const handleFieldChange = (index, key, value) => {
    setModel((prevModel) => ({
      ...prevModel,
      fields: prevModel.fields.map((field, i) =>
        i === index ? { ...field, [key]: value } : field
      )
    }))
  }

  const addField = () => {
    setModel((prevModel) => ({
      ...prevModel,
      fields: [...prevModel.fields, { name: '', type: 'text' }]
    }))
  }

  const confirmDeleteField = (index) => {
    setFieldToDelete(index);
    onOpen();
  }

  const removeField = () => {
    if (fieldToDelete !== null) {
      setModel((prevModel) => ({
        ...prevModel,
        fields: prevModel.fields.filter((_, i) => i !== fieldToDelete)
      }))
      setFieldToDelete(null);
    }
    onClose();
  }

  // Handle drag and drop reordering
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = model.fields.findIndex((_, index) => `field-${index}` === active.id)
      const newIndex = model.fields.findIndex((_, index) => `field-${index}` === over.id)

      setModel(prevModel => ({
        ...prevModel,
        fields: arrayMove(prevModel.fields, oldIndex, newIndex)
      }))

      toast({
        title: 'Field order updated',
        status: 'success',
        duration: 2000
      })
    }
  }

  // Handle taxonomy selection changes
  const handleTaxonomyChange = (selectedTaxonomyIds) => {
    const numericIds = selectedTaxonomyIds
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id)); 
    
    setModel(prevModel => ({
      ...prevModel,
      taxonomy_ids: numericIds
    }));
  }

  const handleSubmit = async () => {
    try {
      if (!model.name.trim()) {
        toast({
          title: 'Validation Error',
          description: 'Model name is required',
          status: 'error',
          duration: 3000
        });
        return;
      }

      const validFields = model.fields.filter(field => field.name.trim());
      if (validFields.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'At least one field with a name is required',
          status: 'error',
          duration: 3000
        });
        return;
      }

      const taxonomyIds = Array.isArray(model.taxonomy_ids) ? model.taxonomy_ids : [];
      
      const payload = {
        ...model,
        fields: validFields,
        taxonomy_ids: taxonomyIds
      };

      await apiClient(`/content-models/${tenantId}/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      toast({
        title: 'Success',
        description: 'Content model updated successfully',
        status: 'success',
        duration: 3000
      })

      navigate('/content-models')
    } catch (error) {
      console.error('Error updating content model:', error)
      toast({
        title: 'Error',
        description: `Failed to update model: ${error.message || 'Unknown error'}`,
        status: 'error',
        duration: 5000
      })
    }
  }

  const getFieldTypeIcon = (type) => {
    const icons = {
      text: "📝",
      richtext: "📄",
      number: "🔢",
      date: "📅",
      boolean: "✅",
      select: "📋",
      multiselect: "📋",
      link: "🔗",
      file: "📎",
      tags: "🏷️",
      json: "{ }"
    };
    
    return icons[type] || "📝";
  };

  if (loading) {
    return (
      <Container maxW="container.lg">
        <VStack align="center" justify="center" height="300px">
          <Spinner size="lg" />
          <Text mt={4}>Loading content model...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" pb={8}>
      <Card mb={6}>
        <CardHeader>
          <Heading size="md">Edit Content Model</Heading>
        </CardHeader>
      </Card>

      <Tabs variant="enclosed" colorScheme="blue" isLazy>
        <TabList mb={4}>
          <Tab>Basic Details</Tab>
          <Tab>Fields</Tab>
          <Tab>Taxonomies</Tab>
        </TabList>

        <TabPanels>
          {/* Basic Details Tab */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Model Information</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={6} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Model Name</FormLabel>
                    <Input 
                      name="name" 
                      value={model.name} 
                      onChange={handleChange} 
                      placeholder="Enter model name" 
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Model Description</FormLabel>
                    <Input 
                      name="description" 
                      value={model.description} 
                      onChange={handleChange} 
                      placeholder="Enter model description" 
                    />
                  </FormControl>
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Fields Tab */}
          <TabPanel>
            <Card mb={4}>
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="md">Fields</Heading>
                  <Button 
                    leftIcon={<FiPlus />} 
                    colorScheme="blue" 
                    size="sm" 
                    onClick={addField}
                  >
                    Add Field
                  </Button>
                </HStack>
              </CardHeader>
              <CardBody>
                {Array.isArray(model.fields) && model.fields.length > 0 ? (
                  <Box>
                    <Flex align="center" mb={4} p={3} bg="blue.50" borderRadius="md" gap={2}>
                      <FiMove color="blue" />
                      <Text fontSize="sm" color="blue.600">
                        Drag and drop fields to reorder them
                      </Text>
                    </Flex>
                    
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={model.fields.map((_, index) => `field-${index}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Accordion allowMultiple defaultIndex={[0]}>
                          {model.fields.map((field, index) => (
                            <SortableFieldItem
                              key={`field-${index}`}
                              field={field}
                              index={index}
                              onFieldChange={handleFieldChange}
                              onDelete={confirmDeleteField}
                              getFieldTypeIcon={getFieldTypeIcon}
                            />
                          ))}
                        </Accordion>
                      </SortableContext>
                    </DndContext>
                  </Box>
                ) : (
                  <Box textAlign="center" py={8} borderWidth={1} borderRadius="md" borderStyle="dashed">
                    <Text color="gray.500" mb={4}>No fields added yet</Text>
                    <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={addField}>
                      Add Your First Field
                    </Button>
                  </Box>
                )}
              </CardBody>
            </Card>
          </TabPanel>

          {/* Taxonomies Tab */}
          <TabPanel>
            <Card>
              <CardHeader>
                <Heading size="md">Assign Taxonomies</Heading>
              </CardHeader>
              <CardBody>
                {loadingTaxonomies ? (
                  <Text>Loading taxonomies...</Text>
                ) : availableTaxonomies.length === 0 ? (
                  <Text>No taxonomies available. Create taxonomies first.</Text>
                ) : (
                  <VStack spacing={4} align="stretch">
                    <CheckboxGroup 
                      colorScheme="blue" 
                      value={model.taxonomy_ids.map(id => id.toString())} 
                      onChange={handleTaxonomyChange}
                    >
                      <Stack spacing={3} direction="column">
                        {availableTaxonomies.map(tax => (
                          <Checkbox key={tax.id} value={tax.id.toString()}>
                            {tax.name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                    
                    <Divider my={2} />
                    
                    <Box>
                      <Text fontWeight="medium" mb={2}>Selected Taxonomies:</Text>
                      <Flex wrap="wrap" gap={2}>
                        {model.taxonomy_ids.length > 0 ? (
                          model.taxonomy_ids.map(id => {
                            const taxonomy = availableTaxonomies.find(t => t.id === id);
                            return taxonomy ? (
                              <Badge key={id} colorScheme="blue" variant="subtle" p={2}>
                                {taxonomy.name}
                              </Badge>
                            ) : null;
                          })
                        ) : (
                          <Text fontSize="sm" color="gray.500">No taxonomies selected</Text>
                        )}
                      </Flex>
                    </Box>
                  </VStack>
                )}
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Action buttons */}
      <Flex justify="flex-end" mt={6} gap={4}>
        <Button onClick={() => navigate('/content-models')}>Cancel</Button>
        <Button 
          colorScheme="blue" 
          onClick={handleSubmit} 
          isDisabled={!model.name || model.fields.length === 0}
        >
          Save Changes
        </Button>
      </Flex>

      {/* Confirmation Dialog for field deletion */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Field
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this field? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={removeField} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  )
}

export default EditContentModel
